import React, { useState, useEffect } from "react";
import { CheckCircleIcon, ChevronDoubleRightIcon, EyeIcon, XCircleIcon } from "@heroicons/react/20/solid";
import Viewsubsteps from "./viewsubsteps";

export default function ProcessViewer() {
  const [openProcessForm, setOpenProcessForm] = useState(false);
  const [openSteps, setOpenSteps] = useState(false);
  const [openSubstepViewer, setOpenSubstepViewer] = useState(null); // Store selected substep
  const [steps, setSteps] = useState([]);
  const [directory, setDirectory] = useState({});
  const [processId, setProcessId] = useState(null);
  const [nameProcess, setNameProcess] = useState("");

  
  useEffect(() => {
    getProcess();
  }, []);

  const getProcess = async () => {
    try {
      const response = await fetch("https://api.insightos.co.uk/api/process", {
        method: "GET",
        headers: { }, credentials: 'include',
      });
      const data = await response.json();

      if (data.success) {
        const dir = data.data.reduce((acc, proc) => {
          const letter = proc.Name[0].toUpperCase();
          if (!acc[letter]) acc[letter] = [];
          acc[letter].push(proc);
          acc[letter].sort((a, b) => a.Name.localeCompare(b.Name));
          return acc;
        }, {});

        setDirectory(dir);
      } else {
        console.error("Failed to fetch processes:", data.message);
      }
    } catch (error) {
      console.error("Error fetching processes:", error);
    }
  };

  const handleGetSteps = async (id, name) => {
    setOpenSteps(true);
    setNameProcess(name);
    setProcessId(id);

    try {
      const response = await fetch(`https://api.insightos.co.uk/api/getsteps/${id}`, {
        method: "GET",
        headers: { }, credentials: 'include',
      });
      const data = await response.json();

      if (response.ok && data.success) {
        setSteps(data.data);
      } else {
        console.error("Failed to fetch steps:", data.message);
        alert(data.message || "Error fetching tasks.");
      }
    } catch (error) {
      console.error("Error fetching steps:", error);
    }
  };

  return (
    <div className="flex h-full">
      {/* Directory (1/3 Width) */}
      <div className="w-1/3 border-r border-gray-200 p-6 overflow-y-auto">
        <button
          onClick={() => setOpenProcessForm(true)}
          data-component="process-viewer"
          data-feature="open_process_creator"
          type="button"
          className="w-full mb-4 inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
        >
          Create New Process <CheckCircleIcon className="h-5 w-5" />
        </button>

        <nav aria-label="Directory" className="h-full overflow-y-auto">
          {Object.keys(directory)
            .sort()
            .map((letter) => (
              <div key={letter} className="mb-4">
                <div className="sticky top-0 z-10 border-b bg-gray-50 px-3 py-1.5 text-sm font-semibold text-gray-900">
                  {letter}
                </div>
                {directory[letter].map((proc) => (
                  <div
                    key={proc.id}
                    className="w-full flex justify-between items-center mt-3 px-3 py-4 bg-emerald-500 rounded-lg shadow-md text-white cursor-pointer"
                    onClick={() => handleGetSteps(proc.id, proc.Name)}
                    data-component="process-directory"
                    data-feature="view_process_steps"
                    data-process-id={proc.id}
                    data-process-name={proc.Name}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold">{proc.Name}</p>
                      <p className="mt-1 truncate text-xs">{proc.Description}</p>
                      <p className="mt-1 text-xs">{proc.StaffName}</p>
                    </div>
                    <ChevronDoubleRightIcon className="w-8 h-8" />
                  </div>
                ))}
              </div>
            ))}
        </nav>
      </div>

      {/* Steps Viewer (2/3 Width) */}
      <div className="w-2/3 p-6 overflow-y-auto">
        {openSteps && (
          <>
            <h2 className="text-lg font-bold mb-3">Steps for {nameProcess}</h2>
            <ul className="divide-y divide-gray-100">
              {steps.map((stepItem) => (
                <li key={stepItem.id} className="flex justify-between gap-x-6 py-4 bg-sky-100 rounded-md shadow-md mb-2">
                  <div className="flex gap-x-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-sky-400 text-white font-semibold">
                      {stepItem.Step}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{stepItem.Task}</p>
                      <p className="mt-1 text-xs text-gray-900">{stepItem.Description}</p>
                      <p className="mt-1 text-xs text-gray-900">Author: {stepItem.StaffName}</p>
                      <div className="mt-2">
                        {!openSubstepViewer ? (
                          <button
                            onClick={() => setOpenSubstepViewer(stepItem)}
                            data-component="process-steps"
                            data-feature="view_substeps"
                            data-step-id={stepItem.id}
                            type="button"
                            className="inline-flex items-center gap-x-1 rounded-md bg-sky-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
                          >
                            View Sub-Steps <EyeIcon className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => setOpenSubstepViewer(null)}
                            data-component="process-steps"
                            data-feature="close_substeps"
                            data-step-id={stepItem.id}
                            type="button"
                            className="inline-flex items-center gap-x-1 rounded-md bg-red-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-600"
                          >
                            Close Sub-Steps <XCircleIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="hidden sm:block bg-indigo-600 text-white px-3 py-1 rounded-lg shadow-lg text-sm">
                    {stepItem.Role}
                  </p>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Substeps Viewer */}
        {openSubstepViewer && (
          <Viewsubsteps taskData={openSubstepViewer.id} setOpenSubstepViewer={setOpenSubstepViewer} />
        )}
      </div>
    </div>
  );
}
